import { Controller } from "@hotwired/stimulus"
import { account_avatar_upload_path, account_avatar_path, account_avatar_status_path } from '../routes'

export default class extends Controller {
  static targets = ["input", "preview", "removeInput"]
  static values = { 
    uploadUrl: String,
    csrfToken: String,
    loadingText: String,
    uploadErrorText: String,
    removeErrorText: String,
    confirmRemoveText: String,
    isProcessing: Boolean
  }

  connect() {
    console.log("Avatar controller connected")
    this.pollInterval = null
    
    // If the avatar is already processing when the page loads, start polling
    if (this.isProcessingValue) {
      console.log("Avatar is processing, starting poll")
      this.startPolling()
    }
  }

  disconnect() {
    this.stopPolling()
  }

  selectFile() {
    this.inputTarget.click()
  }

  handleFileChange(event) {
    const file = event.target.files[0]
    if (file) {
      // Don't show preview - immediately show loading and upload
      this.uploadFile(file)
    }
  }

  async uploadFile(file) {
    const formData = new FormData()
    formData.append('user[avatar]', file)
    
    this.showLoading()
    
    try {
      const response = await fetch(account_avatar_upload_path(), {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': this.csrfTokenValue,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })
      
      if (response.ok) {
        // Success - let Turbo handle the response
        const result = await response.text()
        // Parse and execute the Turbo Stream response
        Turbo.renderStreamMessage(result)
        
        // Start polling for processing status
        this.startPolling()
      } else {
        // Error - restore original content and show error
        this.showError(this.uploadErrorTextValue)
      }
    } catch (error) {
      console.error('Error:', error)
      this.showError(this.uploadErrorTextValue)
    }
  }

  startPolling() {
    // Show loading immediately
    this.showLoadingOnAvatar()
    
    // Poll every 2 seconds
    this.pollInterval = setInterval(async () => {
      await this.checkStatus()
    }, 2000)
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }

  async checkStatus() {
    try {
      const response = await fetch(account_avatar_status_path(), {
        method: 'GET',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue,
          'Accept': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        
        if (data.ready && !data.processing) {
          // Processing complete - stop polling and reload the avatar section
          this.stopPolling()
          this.reloadAvatarSection()
        } else if (!data.processing && !data.ready) {
          // Something went wrong - stop polling and show error
          this.stopPolling()
          this.hideLoading()
        }
        // If still processing, continue polling
      }
    } catch (error) {
      console.error('Error checking avatar status:', error)
      // Don't stop polling on transient errors
    }
  }

  async reloadAvatarSection() {
    try {
      // Fetch the updated avatar section via Turbo Stream
      const response = await fetch(account_avatar_status_path(), {
        method: 'GET',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })
      
      if (response.ok) {
        const html = await response.text()
        // Let Turbo handle the stream response to update just the avatar section
        Turbo.renderStreamMessage(html)
      } else {
        // Fallback: just hide the loading overlay
        this.hideLoading()
      }
    } catch (error) {
      console.error('Error reloading avatar section:', error)
      // Fallback: just hide the loading overlay
      this.hideLoading()
    }
  }

  async removeAvatar() {
    if (confirm(this.confirmRemoveTextValue)) {
      // Show loading overlay on the current avatar image
      this.showLoadingOnAvatar()
      
      try {
        const response = await fetch(account_avatar_path(), {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': this.csrfTokenValue,
            'Accept': 'text/vnd.turbo-stream.html'
          }
        })
        
        if (response.ok) {
          // Success - let Turbo handle the response
          const result = await response.text()
          // Parse and execute the Turbo Stream response
          Turbo.renderStreamMessage(result)
        } else {
          // Error - restore original content and show error
          this.hideLoading()
          this.showError(this.removeErrorTextValue)
        }
      } catch (error) {
        console.error('Error:', error)
        this.hideLoading()
        this.showError(this.removeErrorTextValue)
      }
    }
  }

  showLoading() {
    // If there's a placeholder, show loading overlay on it
    const placeholder = this.previewTarget.querySelector('.avatar-placeholder')
    if (placeholder) {
      const loadingOverlay = placeholder.querySelector('.loading-overlay')
      if (loadingOverlay) {
        loadingOverlay.classList.remove('d-none')
        return
      }
    }
    
    // Fallback to template if no placeholder
    const template = document.getElementById('avatar-loading-template')
    const clone = template.content.cloneNode(true)
    this.previewTarget.innerHTML = ''
    this.previewTarget.appendChild(clone)
  }

  showLoadingOnAvatar() {
    // Show loading overlay on current avatar image
    const avatarContainer = this.previewTarget.querySelector('.avatar-image-container')
    if (avatarContainer) {
      const loadingOverlay = avatarContainer.querySelector('.loading-overlay')
      if (loadingOverlay) {
        loadingOverlay.classList.remove('d-none')
        return
      }
    }
    
    // Fallback to regular loading
    this.showLoading()
  }

  hideLoading() {
    // Hide loading overlay if it exists
    const loadingOverlay = this.previewTarget.querySelector('.loading-overlay')
    if (loadingOverlay) {
      loadingOverlay.classList.add('d-none')
    }
  }

  showError(message) {
    this.hideLoading()
    const template = document.getElementById('avatar-placeholder-template')
    const clone = template.content.cloneNode(true)
    this.previewTarget.innerHTML = ''
    this.previewTarget.appendChild(clone)
    alert(message)
  }
}
